import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

const FileNameSchema = z.string().min(1).max(1000);
const FileSizeSchema = z.number().int().min(0);
const MimeTypeSchema = z.string().min(1).max(255);
const FileHashSchema = z.string().min(1).max(255);
const StorageKeySchema = z.string().min(1);
const UploadStatusSchema = z.enum(['pending', 'completed', 'failed']);

export const FileSchema = z.object({
  fileId: PrimaryKeySchema,
  teamId: PrimaryKeySchema,
  fileName: FileNameSchema,
  fileSize: FileSizeSchema,
  mimeType: MimeTypeSchema,
  fileHash: FileHashSchema,
  storageKey: StorageKeySchema,
  uploadStatus: UploadStatusSchema,
  uploadedBy: PrimaryKeySchema.nullable(),
  metadata: z.record(z.unknown()).default({}),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type File = z.infer<typeof FileSchema>;

export const FilesSchema = z.array(FileSchema);

export type Files = z.infer<typeof FilesSchema>;

export const FilesQuerySchema = z
  .object({
    fileIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
    teamIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
    fileName: z.string().optional(),
    nameLike: z.string().optional(),
    uploadStatus: UploadStatusSchema.optional(),
    mimeTypes: commaDelimitedList(z.string().array()).optional(),
    fileHashes: commaDelimitedList(z.string().array()).optional(),
    uploadedBy: PrimaryKeySchema.optional(),
    // File size filtering
    fileSizeMin: z.coerce.number().int().min(0).optional(),
    fileSizeMax: z.coerce.number().int().min(0).optional(),
    // Date range filtering
    createdAtGte: z.coerce.date().optional(),
    createdAtLte: z.coerce.date().optional(),
    updatedAtGte: z.coerce.date().optional(),
    updatedAtLte: z.coerce.date().optional(),
    // File extension filtering
    fileExtensions: commaDelimitedList(z.string().array()).optional(),
    // Storage key prefix filtering
    storageKeyPrefix: z.string().optional(),
  })
  .merge(
    createOffsetPaginationRequestQuerySchema(
      z.enum(['createdAt', 'updatedAt', 'fileName', 'fileSize']),
    ),
  );

export type FilesQuery = z.infer<typeof FilesQuerySchema>;

export const CreateFileRequestSchema = z.object({
  teamId: PrimaryKeySchema,
  fileName: FileNameSchema,
  fileSize: FileSizeSchema,
  mimeType: MimeTypeSchema,
  fileHash: FileHashSchema,
  storageKey: StorageKeySchema,
  uploadedBy: PrimaryKeySchema.nullable().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export type CreateFileRequest = z.infer<typeof CreateFileRequestSchema>;

export const UpdateFileRequestSchema = z.object({
  fileName: FileNameSchema.optional(),
  uploadStatus: UploadStatusSchema.optional(),
  metadata: z.record(z.unknown()).optional(),
});

export type UpdateFileRequest = z.infer<typeof UpdateFileRequestSchema>;
